function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useState, useCallback, useEffect } from 'react';
import { FIELD_TYPES } from '../constants/fieldTypes';
import { FIELD_ICONS } from './Icons';
import { createField, getAvailableFields } from './FormBuilder/utils';
import { PropertiesPanel } from './FormBuilder/PropertiesPanel/PropertiesPanel';
import { FieldPreview } from './FormBuilder/FieldPreview';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function FormBuilder(_ref) {
  let {
    onSave,
    initialSchema = null
  } = _ref;
  const [fields, setFields] = useState(initialSchema?.fields || []);
  const [selectedField, setSelectedField] = useState(null);
  const [draggedFieldType, setDraggedFieldType] = useState(null);
  const [propertiesCollapsed, setPropertiesCollapsed] = useState(false);

  // Accordion state management
  const [accordionStates, setAccordionStates] = useState({
    validations: false,
    conditions: false
  });

  // Toggle accordion section
  const toggleAccordion = useCallback(section => {
    setAccordionStates(prev => _objectSpread(_objectSpread({}, prev), {}, {
      [section]: !prev[section]
    }));
  }, []);

  // Sync with initialSchema when it changes
  useEffect(() => {
    if (initialSchema) {
      setFields(initialSchema.fields || []);
    }
  }, [initialSchema]);

  // Auto-save when fields, title, or description change
  useEffect(() => {
    const schema = {
      fields,
      settings: {
        showProgressBar: false,
        showPageTitles: true,
        confirmationMessage: 'Thank you for your submission!'
      }
    };
    if (onSave) {
      onSave(schema);
    }
  }, [fields, onSave]);
  const addFieldFromPalette = useCallback(fieldType => {
    const newField = createField(fieldType, fields.length);
    setFields(prev => [...prev, newField]);
    setSelectedField(newField.id);
  }, [fields.length]);
  const removeField = useCallback(fieldId => {
    setFields(prev => prev.filter(field => field.id !== fieldId));
    if (selectedField === fieldId) {
      setSelectedField(null);
    }
  }, [selectedField]);
  const updateField = useCallback((fieldId, updates) => {
    setFields(prev => prev.map(field => field.id === fieldId ? _objectSpread(_objectSpread({}, field), updates) : field));
  }, []);
  const duplicateField = useCallback(fieldId => {
    const fieldToDuplicate = fields.find(f => f.id === fieldId);
    if (fieldToDuplicate) {
      const newField = _objectSpread(_objectSpread({}, fieldToDuplicate), {}, {
        id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        label: `${fieldToDuplicate.label} (Copy)`
      });
      setFields(prev => [...prev, newField]);
    }
  }, [fields]);
  const moveField = useCallback((fieldId, direction) => {
    setFields(prev => {
      const currentIndex = prev.findIndex(field => field.id === fieldId);
      if (currentIndex === -1) return prev;
      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= prev.length) return prev;
      const newFields = [...prev];
      [newFields[currentIndex], newFields[newIndex]] = [newFields[newIndex], newFields[currentIndex]];
      return newFields;
    });
  }, []);

  // Drag and Drop handlers
  const handleDragStart = useCallback((e, fieldType) => {
    setDraggedFieldType(fieldType);
    e.dataTransfer.effectAllowed = 'copy';
  }, []);
  const handleDragOver = useCallback(e => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);
  const handleDrop = useCallback(e => {
    e.preventDefault();
    if (draggedFieldType) {
      addFieldFromPalette(draggedFieldType);
      setDraggedFieldType(null);
    }
  }, [draggedFieldType, addFieldFromPalette]);
  const selectedFieldData = fields.find(f => f.id === selectedField);
  const addOption = useCallback(() => {
    if (selectedField && selectedFieldData) {
      const newOption = {
        label: `Option ${(selectedFieldData.options?.length || 0) + 1}`,
        value: `option${(selectedFieldData.options?.length || 0) + 1}`
      };
      updateField(selectedField, {
        options: [...(selectedFieldData.options || []), newOption]
      });
    }
  }, [selectedField, selectedFieldData, updateField]);
  const updateOption = useCallback((index, field, value) => {
    if (selectedField && selectedFieldData) {
      const updatedOptions = selectedFieldData.options?.map((option, i) => i === index ? _objectSpread(_objectSpread({}, option), {}, {
        [field]: value
      }) : option) || [];
      updateField(selectedField, {
        options: updatedOptions
      });
    }
  }, [selectedField, selectedFieldData, updateField]);
  const removeOption = useCallback(index => {
    if (selectedField && selectedFieldData) {
      const updatedOptions = selectedFieldData.options?.filter((_, i) => i !== index) || [];
      updateField(selectedField, {
        options: updatedOptions
      });
    }
  }, [selectedField, selectedFieldData, updateField]);

  // Repeater field handlers
  const addRepeaterField = useCallback(() => {
    if (selectedField && selectedFieldData) {
      const currentFields = selectedFieldData.fields || [];
      const newField = {
        id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        label: `Field ${currentFields.length + 1}`,
        type: 'text',
        placeholder: '',
        required: false,
        helpText: ''
      };
      updateField(selectedField, {
        fields: [...currentFields, newField]
      });
    }
  }, [selectedField, selectedFieldData, updateField]);
  const updateRepeaterField = useCallback((index, updates) => {
    if (selectedField && selectedFieldData) {
      const updatedFields = selectedFieldData.fields?.map((field, i) => i === index ? _objectSpread(_objectSpread({}, field), updates) : field) || [];
      updateField(selectedField, {
        fields: updatedFields
      });
    }
  }, [selectedField, selectedFieldData, updateField]);
  const removeRepeaterField = useCallback(index => {
    if (selectedField && selectedFieldData) {
      const updatedFields = selectedFieldData.fields?.filter((_, i) => i !== index) || [];
      updateField(selectedField, {
        fields: updatedFields
      });
    }
  }, [selectedField, selectedFieldData, updateField]);

  // Validation handlers
  const updateValidation = useCallback((validationType, value) => {
    if (selectedField && selectedFieldData) {
      const currentValidations = selectedFieldData.validations || {};
      const updatedValidations = _objectSpread({}, currentValidations);
      if (value === '' || value === null || value === undefined) {
        delete updatedValidations[validationType];
      } else {
        updatedValidations[validationType] = value;
      }
      updateField(selectedField, {
        validations: updatedValidations
      });
    }
  }, [selectedField, selectedFieldData, updateField]);

  // Conditions handlers
  const addCondition = useCallback(() => {
    if (selectedField && selectedFieldData) {
      const currentConditions = selectedFieldData.conditions || [];
      const newCondition = {
        sourceFieldId: '',
        operator: 'equals',
        value: ''
      };
      updateField(selectedField, {
        conditions: [...currentConditions, newCondition]
      });
    }
  }, [selectedField, selectedFieldData, updateField]);
  const updateCondition = useCallback((index, field, value) => {
    if (selectedField && selectedFieldData) {
      const updatedConditions = selectedFieldData.conditions?.map((condition, i) => i === index ? _objectSpread(_objectSpread({}, condition), {}, {
        [field]: value
      }) : condition) || [];
      updateField(selectedField, {
        conditions: updatedConditions
      });
    }
  }, [selectedField, selectedFieldData, updateField]);
  const removeCondition = useCallback(index => {
    if (selectedField && selectedFieldData) {
      const updatedConditions = selectedFieldData.conditions?.filter((_, i) => i !== index) || [];
      updateField(selectedField, {
        conditions: updatedConditions
      });
    }
  }, [selectedField, selectedFieldData, updateField]);
  return /*#__PURE__*/_jsxs("div", {
    className: "modern-form-builder",
    children: [/*#__PURE__*/_jsxs("div", {
      className: "builder-palette",
      children: [/*#__PURE__*/_jsxs("div", {
        className: "palette-header",
        children: [/*#__PURE__*/_jsx("h3", {
          children: "Form Components"
        }), /*#__PURE__*/_jsx("p", {
          children: "Drag components to add them to your form"
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: "palette-categories",
        children: [/*#__PURE__*/_jsxs("div", {
          className: "category",
          children: [/*#__PURE__*/_jsx("h4", {
            children: "Layout & Content"
          }), /*#__PURE__*/_jsx("div", {
            className: "component-grid",
            children: ['heading', 'paragraph', 'divider', 'tabs'].map(key => {
              if (!FIELD_TYPES[key]) return null;
              const type = FIELD_TYPES[key];
              const IconComponent = FIELD_ICONS[key];
              return /*#__PURE__*/_jsxs("div", {
                className: "component-item",
                draggable: true,
                onDragStart: e => handleDragStart(e, key),
                onClick: () => addFieldFromPalette(key),
                children: [/*#__PURE__*/_jsx("div", {
                  className: "component-icon",
                  children: IconComponent ? /*#__PURE__*/_jsx(IconComponent, {
                    size: 20
                  }) : type.icon
                }), /*#__PURE__*/_jsx("div", {
                  className: "component-label",
                  children: type.label
                })]
              }, key);
            })
          })]
        }), /*#__PURE__*/_jsxs("div", {
          className: "category",
          children: [/*#__PURE__*/_jsx("h4", {
            children: "Basic Fields"
          }), /*#__PURE__*/_jsx("div", {
            className: "component-grid",
            children: ['text', 'email', 'number', 'password', 'url', 'tel', 'search', 'textarea', 'hidden', 'currency', 'price'].map(key => {
              if (!FIELD_TYPES[key]) return null;
              const type = FIELD_TYPES[key];
              const IconComponent = FIELD_ICONS[key];
              return /*#__PURE__*/_jsxs("div", {
                className: "component-item",
                draggable: true,
                onDragStart: e => handleDragStart(e, key),
                onClick: () => addFieldFromPalette(key),
                children: [/*#__PURE__*/_jsx("div", {
                  className: "component-icon",
                  children: IconComponent ? /*#__PURE__*/_jsx(IconComponent, {
                    size: 20
                  }) : type.icon
                }), /*#__PURE__*/_jsx("div", {
                  className: "component-label",
                  children: type.label
                })]
              }, key);
            })
          })]
        }), /*#__PURE__*/_jsxs("div", {
          className: "category",
          children: [/*#__PURE__*/_jsx("h4", {
            children: "Selection Fields"
          }), /*#__PURE__*/_jsx("div", {
            className: "component-grid",
            children: ['select', 'radio', 'checkbox', 'switch', 'tags'].map(key => {
              if (!FIELD_TYPES[key]) return null;
              const type = FIELD_TYPES[key];
              const IconComponent = FIELD_ICONS[key];
              return /*#__PURE__*/_jsxs("div", {
                className: "component-item",
                draggable: true,
                onDragStart: e => handleDragStart(e, key),
                onClick: () => addFieldFromPalette(key),
                children: [/*#__PURE__*/_jsx("div", {
                  className: "component-icon",
                  children: IconComponent ? /*#__PURE__*/_jsx(IconComponent, {
                    size: 20
                  }) : type.icon
                }), /*#__PURE__*/_jsx("div", {
                  className: "component-label",
                  children: type.label
                })]
              }, key);
            })
          })]
        }), /*#__PURE__*/_jsxs("div", {
          className: "category",
          children: [/*#__PURE__*/_jsx("h4", {
            children: "Date & Time"
          }), /*#__PURE__*/_jsx("div", {
            className: "component-grid",
            children: ['date', 'time', 'datetime-local', 'month', 'week'].map(key => {
              if (!FIELD_TYPES[key]) return null;
              const type = FIELD_TYPES[key];
              const IconComponent = FIELD_ICONS[key];
              return /*#__PURE__*/_jsxs("div", {
                className: "component-item",
                draggable: true,
                onDragStart: e => handleDragStart(e, key),
                onClick: () => addFieldFromPalette(key),
                children: [/*#__PURE__*/_jsx("div", {
                  className: "component-icon",
                  children: IconComponent ? /*#__PURE__*/_jsx(IconComponent, {
                    size: 20
                  }) : type.icon
                }), /*#__PURE__*/_jsx("div", {
                  className: "component-label",
                  children: type.label
                })]
              }, key);
            })
          })]
        }), /*#__PURE__*/_jsxs("div", {
          className: "category",
          children: [/*#__PURE__*/_jsx("h4", {
            children: "File & Media"
          }), /*#__PURE__*/_jsx("div", {
            className: "component-grid",
            children: ['file', 'attachment', 'image'].map(key => {
              if (!FIELD_TYPES[key]) return null;
              const type = FIELD_TYPES[key];
              const IconComponent = FIELD_ICONS[key];
              return /*#__PURE__*/_jsxs("div", {
                className: "component-item",
                draggable: true,
                onDragStart: e => handleDragStart(e, key),
                onClick: () => addFieldFromPalette(key),
                children: [/*#__PURE__*/_jsx("div", {
                  className: "component-icon",
                  children: IconComponent ? /*#__PURE__*/_jsx(IconComponent, {
                    size: 20
                  }) : type.icon
                }), /*#__PURE__*/_jsx("div", {
                  className: "component-label",
                  children: type.label
                })]
              }, key);
            })
          })]
        }), /*#__PURE__*/_jsxs("div", {
          className: "category",
          children: [/*#__PURE__*/_jsx("h4", {
            children: "Advanced Fields"
          }), /*#__PURE__*/_jsx("div", {
            className: "component-grid",
            children: ['range', 'rating', 'repeater', /* 'color', */'signature'].map(key => {
              if (!FIELD_TYPES[key]) return null;
              const type = FIELD_TYPES[key];
              const IconComponent = FIELD_ICONS[key];
              return /*#__PURE__*/_jsxs("div", {
                className: "component-item",
                draggable: true,
                onDragStart: e => handleDragStart(e, key),
                onClick: () => addFieldFromPalette(key),
                children: [/*#__PURE__*/_jsx("div", {
                  className: "component-icon",
                  children: IconComponent ? /*#__PURE__*/_jsx(IconComponent, {
                    size: 20
                  }) : type.icon
                }), /*#__PURE__*/_jsx("div", {
                  className: "component-label",
                  children: type.label
                })]
              }, key);
            })
          })]
        })]
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: `builder-canvas ${propertiesCollapsed ? 'canvas-expanded' : ''}`,
      children: /*#__PURE__*/_jsx("div", {
        className: "canvas-dropzone",
        onDragOver: handleDragOver,
        onDrop: handleDrop,
        children: (() => {
          // Filter out fields that are inside containers (tabs, tables)
          const isFieldInContainer = fieldId => {
            return fields.some(f => {
              const containerTypes = ['tabs', 'table'];
              if (containerTypes.includes(f.type)) {
                // Check sections (for table - legacy)
                if (f.type === 'table') {
                  const sections = f.sections || [];
                  if (sections.includes(fieldId)) {
                    return true;
                  }
                  // Check cells in columns (for table - new structure)
                  const columns = f.columns || [];
                  for (const col of columns) {
                    const cells = col.cells || {};
                    const cellValues = Object.values(cells);
                    const hasField = cellValues.some(cell => cell && cell.type === 'field' && cell.value === fieldId);
                    if (hasField) {
                      return true;
                    }
                  }
                }
                // For tabs, check tabContent
                if (f.type === 'tabs' && f.tabContent) {
                  const tabContent = f.tabContent;
                  const allFieldIds = Object.values(tabContent).flat().filter(item => item.type === 'field').map(item => item.value);
                  if (allFieldIds.includes(fieldId)) {
                    return true;
                  }
                }
              }
              return false;
            });
          };
          const topLevelFields = fields.filter(f => {
            const containerTypes = ['tabs', 'table'];
            // Show container fields only if they're not inside another container
            // Show regular fields only if they're not inside any container
            if (containerTypes.includes(f.type)) {
              // For container fields, check if they're inside another container
              return !isFieldInContainer(f.id);
            }
            // For regular fields, show if not in any container
            return !isFieldInContainer(f.id);
          });
          if (topLevelFields.length === 0) {
            return /*#__PURE__*/_jsxs("div", {
              className: "empty-canvas",
              children: [/*#__PURE__*/_jsx("div", {
                className: "empty-icon",
                children: "\uD83D\uDCDD"
              }), /*#__PURE__*/_jsx("h3", {
                children: "Start Building Your Form"
              }), /*#__PURE__*/_jsxs("p", {
                children: ["Drag components from the left panel", /*#__PURE__*/_jsx("br", {}), "or click on them to add to your form"]
              })]
            });
          }
          return /*#__PURE__*/_jsx("div", {
            className: "form-preview",
            children: /*#__PURE__*/_jsx("div", {
              className: "form-preview-fields-container",
              children: topLevelFields.map((field, index) => /*#__PURE__*/_jsx(FieldPreview, {
                field: field,
                isSelected: selectedField === field.id,
                onSelect: () => setSelectedField(field.id),
                allFields: fields,
                index: index,
                totalFields: topLevelFields.length,
                onMoveUp: id => moveField(id, 'up'),
                onMoveDown: id => moveField(id, 'down'),
                onDuplicate: duplicateField,
                onDelete: removeField
              }, field.id))
            })
          });
        })()
      })
    }), /*#__PURE__*/_jsx(PropertiesPanel, {
      selectedFieldData: selectedFieldData,
      selectedField: selectedField,
      fields: fields,
      updateField: updateField,
      updateValidation: updateValidation,
      updateCondition: updateCondition,
      removeCondition: removeCondition,
      addCondition: addCondition,
      accordionStates: accordionStates,
      toggleAccordion: toggleAccordion,
      updateOption: updateOption,
      removeOption: removeOption,
      addOption: addOption,
      updateRepeaterField: updateRepeaterField,
      removeRepeaterField: removeRepeaterField,
      addRepeaterField: addRepeaterField,
      collapsed: propertiesCollapsed,
      onToggleCollapse: () => setPropertiesCollapsed(!propertiesCollapsed)
    })]
  });
}
export default FormBuilder;